import React, { ReactNode } from 'react';
import { AppBar, Box, Button, Dialog, IconButton, Popover, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

type FilterShellProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onApply?: () => void;
  onReset?: () => void;
  children: ReactNode;
};

export const FilterShell = ({ anchorEl, open, onClose, onApply, onReset, children }: FilterShellProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  if (isDesktop) {
    return (
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
        slotProps={{ paper: { sx: { mt: 2, display: 'flex', flexDirection: 'column', maxHeight: '80vh' } } }}>
        <Box sx={{ minWidth: 360, maxWidth: 440, flex: 1, overflowY: 'auto' }}>{children}</Box>
        {onApply && (
          <Box
            sx={{
              p: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              bgcolor: 'background.paper',
            }}>
            <Button variant="contained" onClick={onApply} sx={{ textTransform: 'none' }}>
              {t('apply')}
            </Button>
          </Box>
        )}
      </Popover>
    );
  }

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar
        sx={{ position: 'sticky', display: 'flex', flexDirection: 'row', alignItems: 'center', px: 1, py: 0.5 }}
        elevation={0}
        color="default">
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        {onReset && (
          <Button onClick={onReset} sx={{ textTransform: 'none', color: 'primary.main' }}>
            {t('reset')}
          </Button>
        )}
      </AppBar>

      <Box sx={{ flex: 1, overflowY: 'auto', pb: 10 }}>{children}</Box>

      {onApply && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            insetInline: 0,
            p: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}>
          <Button fullWidth variant="contained" onClick={onApply} sx={{ textTransform: 'none', py: 1.5 }}>
            {t('apply')}
          </Button>
        </Box>
      )}
    </Dialog>
  );
};
